import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { sanitizeHtml } from '../../assets/utils/comm/sanitize';

/**
 * 리치 텍스트 에디터 컴포넌트
 * 원본: components/rich-text-editor.html
 * Quill.js를 사용합니다 (npm 패키지: react-quill).
 * 전역 quill 인스턴스를 window.quill로 관리 (KanbanBoard에서 사용)
 *
 * @example
 * // RichTextEditor 컴포넌트 사용 예시
 * import RichTextEditor from './components/widgets/RichTextEditor';
 * import { useRef, useState } from 'react';
 *
 * const MyPage = () => {
 *   const editorRef = useRef(null);
 *   const [content, setContent] = useState('');
 *
 *   const editorData = {
 *     id: 'myRichTextEditor',
 *     placeholder: '내용을 입력하세요...'
 *   };
 *
 *   const handleChange = (value) => {
 *     setContent(value);
 *   };
 *
 *   return (
 *     <div>
 *       <RichTextEditor
 *         ref={editorRef}
 *         data={editorData}
 *         onChange={handleChange}
 *       />
 *     </div>
 *   );
 * };
 */
const RichTextEditor = forwardRef(({ data, onChange }, ref) => {
  const quillRef = useRef(null);
  const [value, setValue] = useState('');
  const isInitializedRef = useRef(false);
  const contentInitializedRef = useRef(false);
  const lastContentRef = useRef(null);

  // quillOptions와 initialContent를 data에서 가져오기
  const quillOptions = data?.quillOptions || {};
  const initialContent = data?.initialContent;

  // editorId 추출 (data.id 사용)
  const editorId = data?.id || 'rich-text-editor';
  // 에디터 높이(px). 미지정 시 200
  const editorHeight = typeof data?.editorHeight === 'number' ? data.editorHeight : 200;
  const editorInnerHeight = Math.max(0, editorHeight - 2);

  // Quill 인스턴스에 접근하여 전역으로 등록 및 초기 내용 설정
  useEffect(() => {
    const currentQuillRef = quillRef.current;
    if (currentQuillRef) {
      // Quill 인스턴스가 준비될 때까지 약간의 지연
      const timer = setTimeout(() => {
        const quill = currentQuillRef.getEditor();
        if (quill) {
          // 전역 quill 인스턴스로 등록 (KanbanBoard에서 사용 - 하위 호환성)
          window.quill = quill;

          // editorId별로 인스턴스 저장 (여러 에디터 지원)
          window.__quillInstances = window.__quillInstances || {};
          window.__quillInstances[editorId] = quill;

          // initialContent가 Delta 형식이면 설정
          if (initialContent && !isInitializedRef.current) {
            try {
              // Delta 형식인 경우
              if (initialContent.ops && Array.isArray(initialContent.ops)) {
                quill.setContents(initialContent);
                // Delta를 HTML로 변환하여 value 업데이트
                const html = quill.root.innerHTML;
                setValue(html);
              } else if (typeof initialContent === 'string') {
                // HTML 문자열인 경우
                const safeHtml = sanitizeHtml(initialContent);
                quill.root.innerHTML = safeHtml;
                setValue(safeHtml);
              }
              isInitializedRef.current = true;
            } catch (error) {
              console.warn('Error setting initial content:', error);
            }
          }
        }
      }, 100);

      return () => {
        clearTimeout(timer);
        // Cleanup: 전역 인스턴스 정리
        if (currentQuillRef) {
          const quill = currentQuillRef.getEditor();
          if (window.quill === quill) {
            window.quill = null;
          }
          if (window.__quillInstances && window.__quillInstances[editorId] === quill) {
            delete window.__quillInstances[editorId];
          }
        }
      };
    }
  }, [initialContent, editorId]);

  // 외부에서 내용을 설정할 수 있도록 (KanbanBoard에서 사용)
  // data.content가 변경될 때만 에디터 내용을 업데이트 (초기 로드 또는 외부에서 명시적으로 변경된 경우)
  useEffect(() => {
    // data.content가 없거나 undefined인 경우 무시
    if (data?.content === undefined) {
      return;
    }

    // 이전 content와 동일하면 무시 (불필요한 업데이트 방지)
    if (lastContentRef.current === data.content) {
      return;
    }

    // Quill 인스턴스가 준비될 때까지 대기 (약간의 지연 필요)
    const timer = setTimeout(() => {
      if (!quillRef.current) {
        return;
      }

      const quill = quillRef.current.getEditor();
      if (!quill || !quill.root) {
        return;
      }

          // 아직 초기화되지 않았거나, 외부에서 명시적으로 content가 변경된 경우
          if (!contentInitializedRef.current || lastContentRef.current !== data.content) {
            if (typeof data.content === 'string' && data.content.trim() !== '') {
              const safeContent = sanitizeHtml(data.content);
              // 에디터가 비어있거나, 외부에서 명시적으로 변경된 경우에만 설정
              const currentContent = quill.root.innerHTML.trim();
              if (!contentInitializedRef.current) {
                // 초기 로드: 에디터가 비어있을 때만 설정
                if (!currentContent || currentContent === '<p><br></p>') {
                  // Quill의 dangerouslyPasteHTML을 사용하여 HTML을 안전하게 설정
                  try {
                    quill.clipboard.dangerouslyPasteHTML(0, safeContent);
                    const html = quill.root.innerHTML;
                    setValue(html);
                    contentInitializedRef.current = true;
                    lastContentRef.current = data.content;
                  } catch (error) {
                    console.warn('Error setting content with dangerouslyPasteHTML:', error);
                    // 폴백: 직접 innerHTML 설정
                    quill.root.innerHTML = safeContent;
                    setValue(safeContent);
                    contentInitializedRef.current = true;
                    lastContentRef.current = data.content;
                  }
                }
              } else {
                // 외부에서 명시적으로 content가 변경된 경우 (예: 다른 카드 편집)
                try {
                  // 기존 내용을 모두 삭제하고 새 내용으로 교체
                  quill.setContents([], 'silent');
                  quill.clipboard.dangerouslyPasteHTML(0, safeContent);
                  const html = quill.root.innerHTML;
                  setValue(html);
                  lastContentRef.current = data.content;
                } catch (error) {
                  console.warn('Error setting content with dangerouslyPasteHTML:', error);
                  quill.root.innerHTML = safeContent;
                  setValue(safeContent);
                  lastContentRef.current = data.content;
                }
              }
            } else if (typeof data.content === 'string' && data.content.trim() === '') {
              // 빈 내용인 경우
              quill.setContents([], 'silent');
              setValue('');
              contentInitializedRef.current = true;
              lastContentRef.current = data.content;
            }
          }
    }, 150); // Quill 인스턴스가 완전히 준비될 때까지 충분한 시간 대기

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.content]);

  // editorId가 변경되면 초기화 플래그 리셋 (다른 에디터 인스턴스로 전환)
  useEffect(() => {
    contentInitializedRef.current = false;
    lastContentRef.current = null;
  }, [editorId]);

  // ref를 통해 외부에서 접근 가능하도록
  useImperativeHandle(ref, () => ({
    getEditor: () => quillRef.current?.getEditor(),
    getValue: () => value,
    setValue: (newValue) => setValue(newValue),
    getHTML: () => quillRef.current?.getEditor()?.root.innerHTML || '',
    setHTML: (html) => {
      if (quillRef.current) {
        const quill = quillRef.current.getEditor();
        if (quill) {
          quill.root.innerHTML = html;
          setValue(html);
        }
      }
    },
    getContents: () => quillRef.current?.getEditor()?.getContents() || null
  }));

  // quillOptions에서 modules와 formats 추출
  const modules = quillOptions?.modules || {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['link'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['clean']
    ],
  };

  const formats = quillOptions?.formats || [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'link',
    'list', 'bullet'
  ];

  const placeholder = quillOptions?.placeholder || '';

      const handleChange = (newValue) => {
        setValue(newValue);
        // 사용자가 입력할 때마다 lastContentRef 업데이트하여 외부 content와의 충돌 방지
        if (quillRef.current) {
          const quill = quillRef.current.getEditor();
          if (quill && quill.root) {
            // 실제 HTML 내용을 가져와서 저장
            const htmlContent = quill.root.innerHTML;
            lastContentRef.current = htmlContent;
            // contentInitializedRef를 true로 설정하여 사용자가 입력 중임을 표시
            contentInitializedRef.current = true;

            // onChange 콜백이 있으면 호출
            if (onChange && typeof onChange === 'function') {
              onChange(htmlContent);
            }
          }
        }
      };

      return (
        <>
          <style>{`
            .rich-text-editor-wrap {
              display: block;
              position: relative;
              margin-bottom: 2.5rem;
              isolation: isolate;
              background: #fff;
              border-radius: 4px;
            }
            .rich-text-editor-wrap > .quill {
              display: flex;
              flex-direction: column;
              background: #fff;
            }
            .rich-text-editor-wrap .ql-toolbar.ql-snow {
              flex: 0 0 auto;
              background: #fff !important;
              border-color: #e2e8f0;
            }
            .rich-text-editor-wrap .ql-container.ql-snow {
              min-height: ${editorHeight}px;
              height: ${editorHeight}px !important;
              max-height: ${editorHeight}px;
              overflow: hidden;
              flex: 0 0 ${editorHeight}px;
              background: #fff !important;
              border-color: #e2e8f0;
            }
            .rich-text-editor-wrap .ql-editor {
              min-height: ${editorInnerHeight}px;
              max-height: ${editorInnerHeight}px;
              overflow-y: auto;
              box-sizing: border-box;
              background: #fff !important;
            }
          `}</style>
          <div id={editorId} className="rich-text-editor-wrap">
            <ReactQuill
              ref={quillRef}
              theme={quillOptions?.theme || 'snow'}
              value={value}
              onChange={handleChange}
              onChangeSelection={(selection) => {
                // 선택 영역 변경 시 처리 (필요시)
              }}
              modules={modules}
              formats={formats}
              placeholder={placeholder}
              className="quill"
            />
          </div>
        </>
      );
});

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
